import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth/AuthProvider';
import { getTenantItem, setTenantItem, migrateLegacyKey } from '@/lib/tenantStorage';

interface DashboardWidgetPrefs {
  [key: string]: {
    isExpanded: boolean;
  };
}

const STORAGE_KEY = 'dashboard-widget-prefs';

/**
 * Default expansion states for dashboard widgets.
 * Text-based metrics and actionable lists are expanded by default.
 * Visual charts are collapsed by default to keep dashboard lean.
 */
const DEFAULT_PREFS: DashboardWidgetPrefs = {
  // Text-based metrics - expanded by default
  'health-indicators': { isExpanded: true },
  'stats-cards': { isExpanded: true },
  'revenue-goal': { isExpanded: true },
  // Actionable lists - expanded by default
  'upcoming-tasks': { isExpanded: true },
  // Visual charts - collapsed by default
  'revenue-chart': { isExpanded: false },
  'projects-stage-chart': { isExpanded: false },
  // Future 9.9b widgets - text-based/actionable, expanded by default
  'financial-summary': { isExpanded: true },
  'reminders': { isExpanded: true },
  'attention-centre': { isExpanded: true },
  // R1/R2/R3 — Tax estimates + draft invoices, expanded by default
  'tax-estimates': { isExpanded: true },
  // SPEC-monthly-proposal-activity (#151) — current-month headline, expanded by default
  'proposal-activity': { isExpanded: true },
};

/**
 * Hook for managing dashboard widget collapse/expand preferences.
 * Persists user preferences to tenant-scoped localStorage.
 */
export function useDashboardPreferences() {
  const { user } = useAuth();
  const tenantId = user?.tenant_id ?? '';

  const [prefs, setPrefs] = useState<DashboardWidgetPrefs>(() => {
    if (!tenantId) return DEFAULT_PREFS;
    // Migrate legacy un-prefixed key on first access
    migrateLegacyKey(STORAGE_KEY, tenantId);
    const stored = getTenantItem(STORAGE_KEY, tenantId);
    if (stored) {
      try {
        return { ...DEFAULT_PREFS, ...JSON.parse(stored) };
      } catch {
        return DEFAULT_PREFS;
      }
    }
    return DEFAULT_PREFS;
  });

  // Persist to tenant-scoped localStorage on change
  useEffect(() => {
    if (tenantId) {
      setTenantItem(STORAGE_KEY, JSON.stringify(prefs), tenantId);
    }
  }, [prefs, tenantId]);

  const setWidgetExpanded = (widgetKey: string, isExpanded: boolean) => {
    setPrefs((prev) => ({
      ...prev,
      [widgetKey]: { isExpanded },
    }));
  };

  const isWidgetExpanded = (widgetKey: string): boolean => {
    return prefs[widgetKey]?.isExpanded ?? DEFAULT_PREFS[widgetKey]?.isExpanded ?? true;
  };

  return {
    prefs,
    setWidgetExpanded,
    isWidgetExpanded,
  };
}
